import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import GlobalRequest from '@/api/global-request';
import useUserComposable from '@/composables/UserComposable';
import { showToast } from '@/helpers/toast-handler';
import { formatTableDate } from '@/helpers/date-formatter';
import { SharedTypes } from '../../../enum';
import { UserProfileTypes, AttributeSubmissionTypes } from '../enum';
import { AttributeTypes } from '../enum';
import { getAllAttributeTypes } from '../constants';

const COMPANY_ATTRIBUTE_URL = 'v2/template/attributes';
const SHARED_ATTRIBUTE_URL = 'v2/template/attributes/shared';
const TAGGED_ATTRIBUTE_URL = 'v2/template/attributes/tags/attributes';
const SHARED_TAGGED_ATTRIBUTE_URL = 'v2/attributes/shared/codes';

export const useAttributeStore = defineStore('attribute', () => {
    const { company_id } = useUserComposable();

    const input_types = ref(getAllAttributeTypes()); // type of attribute
    // states
    const params = ref({
        company_id: null,
        categories: [],
        code: null,
        code_exact: null,
        used: null, // add filter to handle this in AttributeFilters later
        user_profile: UserProfileTypes.NORMAL,
        default: true,
        tagged: false,
        shared: SharedTypes.COMPANY, // this filter is only for front
        category: null, // this filter is only for front
        page_size: null,
        page: null,
        paginate: null,
    });
    const attribute = ref({
        // overall attribute properties
        company_id: null,
        label: null,
        code: null,
        id: null,
        description: null,
        placeholder: null,
        icon: null,
        formula_id: null,
        category_id: null,
        type: null,
        required: null,
        // normal/shared attribute properties
        unit: null,
        user_profile: false,
        options: null,
        rules: null,
        // tagged attribute properties
        tags: [],
        shared: 0, // shareable flag
    });
    const editId = ref(null);
    const attributes = ref([]);
    const tags = ref([]);
    const sharedTaggedAttributes = ref([]);
    const totalAttributes = ref(null);
    const loading = ref(false);

    // getters
    const allAttributes = computed(() => {
        return attributes.value?.map((attribute) => {
            return {
                ...attribute,
                type_string: input_types.value?.find((type) => type.value === parseInt(attribute.type))?.name,
                created_at: formatTableDate(attribute.created_at),
                tag_id: attribute?.tags[0]?.tag_id ?? null,
            };
        });
    });

    const allUserProfileAttributes = computed(() => {
        return allAttributes.value?.filter((attribute) => ![AttributeTypes.HEADING, AttributeTypes.SUB_HEADING, AttributeTypes.STATIC_TEXT].includes(parseInt(attribute.type)));
    });

    const allTags = computed(() => {
        return tags.value;
    });

    const allSharedTaggedAttributes = computed(() => {
        // filter out allAttributes with sharedTaggedAttributes
        const allSharedCodes = sharedTaggedAttributes.value?.map((obj) => obj.attribute_code);
        return allAttributes.value?.filter((attribute) => {
            return allSharedCodes.includes(attribute.code);
        });
    });

    // actions
    const fetchAllUserProfileAttributes = async () => {
        params.value.user_profile = UserProfileTypes.USER_PROFILE;
        await fetchAllAttributes();
    };

    // By default we are setting paginate false here to avoid issues with existing features that doesnt use pagination option
    const fetchAllAttributes = async (paginate = false) => {
        try {
            loading.value = true;
            const URL = params.value?.shared === SharedTypes.COMPANY ? COMPANY_ATTRIBUTE_URL : SHARED_ATTRIBUTE_URL;
            const bodyParams = {
                ...params.value,
                company_id: params.value?.shared === SharedTypes.COMPANY ? company_id.value : null,
                paginate,
            };
            delete bodyParams.shared;
            delete bodyParams.category;
            const response = await GlobalRequest.getRequest(URL, {
                params: bodyParams,
            });
            if (response.statusCode === 200) {
                attributes.value = paginate ? response.data.data : response.data;
                if (params.value.category) {
                    const supported_types = params.value.category;
                    attributes.value = attributes.value?.filter((attribute) => supported_types.includes(parseInt(attribute.type)));
                }
                totalAttributes.value = response.data.total;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch all attributes');
        } finally {
            loading.value = false;
        }
    };

    // attribute details fetch is only used for company attributes ATM
    const fetchAttributeById = async (attributeId) => {
        try {
            const response = await GlobalRequest.getRequest(`${COMPANY_ATTRIBUTE_URL}/${attributeId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch attribute by id');
        }
    };

    const createAttribute = async (body, submissionType) => {
        try {
            const URL = loadUrl(submissionType);
            // remove some info if its tagged
            if (submissionType === AttributeSubmissionTypes.TAGGED) {
                delete body.options;
                delete body.rules;
                delete body.unit;
            }
            const response = await GlobalRequest.postRequest(URL, {
                ...body,
                company_id: submissionType !== AttributeSubmissionTypes.SHARED ? company_id.value : null,
                description: body.description?.length > 255 ? body.description?.substring(0, 255) : body.description,
            }); // put company id incase its not shared type
            if (response.statusCode === 201) {
                // push the data into the attributes.value
                attributes.value?.push(response.data);
                // if its a tagged attribute and shared in pool, the push it to sharedTaggedAttributes as well
                if (body.shared === 1) {
                    sharedTaggedAttributes.value?.push(response.data);
                }
                showToast(response, 'Attribute created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create attribute');
        }
    };

    const editAttribute = (attributeId) => {
        editId.value = attributeId;
        let attributeData = attributes.value?.find((attribute) => attribute.id === attributeId);
        // check if its a tagged attribute or not before proceeding to edit
        const tags = attributeData?.tags ?? attribute.value?.tags ?? [];
        if (tags && tags.length) {
            // if yes, check if its shared or not as well
            // to do so, compare its tag_id and see if it exists in the list of sharedTaggedAttributes
            const tagId = tags[0]?.tag_id;
            const attributeCode = attributeData.code;
            const existingSharedTag = sharedTaggedAttributes.value?.find((obj) => obj.attribute_code === attributeCode);
            // if it exists then its shared, else its not shared
            attributeData = {
                ...attributeData,
                tags: [{ id: tagId }],
                shared: existingSharedTag ? 1 : 0,
            };
        }

        loadAttributeDetails(attributeData);
    };

    const updateAttribute = async (body, submissionType) => {
        try {
            const URL = loadUrl(submissionType);
            const response = await GlobalRequest.patchRequest(`${URL}/${editId.value}`, {
                ...body,
                description: body.description?.length > 255 ? body.description?.substring(0, 255) : body.description,
                // shared: body.shared ? 1 : 0,
            });
            if (response.statusCode === 200) {
                // push the data into the attributes.value
                showToast(response, 'Attribute updated successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update attribute');
        }
    };

    const deleteAttribute = async (attributeId, submissionType) => {
        try {
            // incase submissionType is not present or null
            if (!submissionType) {
                submissionType = getSubmissionTypeByAttributeId(attributeId);
            }
            const response = await GlobalRequest.deleteRequest(`${COMPANY_ATTRIBUTE_URL}/${attributeId}`, {
                params: {
                    company_id: submissionType !== AttributeSubmissionTypes.SHARED ? company_id.value : null,
                },
            });
            if (response.statusCode === 200) {
                showToast(response, 'Attribute deleted successfully');
                // remove it from the array after deletion
                attributes.value = attributes.value?.filter((attribute) => attribute.id !== attributeId);
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete attribute');
        }
    };

    const setDefaultAttribute = async (attributeId, submissionType) => {
        //console.log('attributeId:', attributeId);
        try {
            if (!submissionType) {
                submissionType = getSubmissionTypeByAttributeId(attributeId);
            }
            const companyId = submissionType !== AttributeSubmissionTypes.SHARED ? company_id.value : null;
            const response = await GlobalRequest.patchRequest(`${COMPANY_ATTRIBUTE_URL}/${attributeId}/default?company_id=${companyId}`);
            if (response.statusCode === 200) {
                await fetchAllAttributes();
                showToast(response, 'Attribute set as default');
            }
        } catch (error) {
            showToast({}, error?.data?.response ?? 'Failed to set attribute as default');
        }
    };

    const transferAttributes = async (attributes, exportFlag) => {
        try {
            // make a loop and export of them
            const total = attributes.length;
            let transferCount = 0;
            for (let i = 0; i < attributes?.length; i++) {
                const attribute = attributes[i];
                // check if tagged attributes are being transferred
                if (attribute.tags && attribute.tags.length) {
                    showToast({ severity: 'warn', summary: 'Error' }, 'Tagged attributes cannot be transferred');
                } else {
                    const transfered = exportFlag ? await exportCompanyToShared(attribute.id) : await importSharedToCompany(attribute.id);
                    // if transfered flag is true then add the count
                    if (transfered) {
                        transferCount++;
                    }
                }
            }
            if (transferCount === total) {
                showToast({ severity: 'success', summary: 'Success' }, `Successfully transfered ${total} attributes`);
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Error while transfering attributes');
        }
    };

    const importSharedToCompany = async (attributeId) => {
        try {
            const response = await GlobalRequest.patchRequest(`${SHARED_ATTRIBUTE_URL}/${attributeId}/import`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 201) {
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to import shared library attributes');
        }
    };

    const exportCompanyToShared = async (attributeId) => {
        try {
            const response = await GlobalRequest.patchRequest(`${COMPANY_ATTRIBUTE_URL}/${attributeId}/export`);
            if (response.statusCode === 201) {
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to export company attribute');
        }
    };

    const loadAttributeFromTagId = (tagId) => {
        try {
            const existingTag = allTags.value?.find((tag) => tag.id === tagId);
            if (existingTag) {
                // load the attribute details inside the tag.attribute
                loadAttributeDetails({ ...existingTag.attribute, tags: [{ id: tagId }] });
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to load tag data');
        }
    };

    // attribute tags actions
    const fetchAllTags = async () => {
        try {
            const response = await GlobalRequest.getRequest(`${COMPANY_ATTRIBUTE_URL}/tags`);
            if (response.statusCode === 200) {
                tags.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch all tags');
        }
    };

    // fetch all tagged attributes which have been shared by the company
    const fetchAllSharedTaggedAttributes = async () => {
        try {
            const response = await GlobalRequest.getRequest(SHARED_TAGGED_ATTRIBUTE_URL, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                sharedTaggedAttributes.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch shared tagged attributes');
        }
    };

    // helpers
    const initAttribute = () => {
        attribute.value.company_id = null;
        attribute.value.label = null;
        attribute.value.code = null;
        attribute.value.id = null;
        attribute.value.description = null;
        attribute.value.placeholder = null;
        attribute.value.icon = null;
        attribute.value.formula_id = null;
        attribute.value.category_id = null;
        attribute.value.type = null;
        attribute.value.required = null;
        // normal/shared attribute properties
        attribute.value.unit = null;
        attribute.value.user_profile = false;
        attribute.value.options = null;
        attribute.value.rules = null;
        // tagged attribute properties
        attribute.value.tags = null;
        attribute.value.shared = 0; // shareable flag;
        editId.value = null;
    };

    const loadUrl = (submissionType) => {
        // CASE 1: tagged attribute
        // CASE 2: normal attribute
        return submissionType === AttributeSubmissionTypes.TAGGED ? TAGGED_ATTRIBUTE_URL : COMPANY_ATTRIBUTE_URL;
    };

    const loadAttributeDetails = (attributeData) => {


        attribute.value = {
            company_id: attributeData?.company_id,
            label: attributeData?.label,
            code: attributeData?.code,
            id: attributeData?.id,
            description: attributeData?.description,
            placeholder: attributeData?.placeholder,
            icon: attributeData?.icon,
            formula_id: attributeData?.formula_id,
            category_id: attributeData?.category_id,
            type: parseInt(attributeData?.type),
            required: attributeData?.required === 1 ? true : false,
            // normal/shared attribute properties
            unit: attributeData?.unit,
            user_profile: attributeData?.user_profile === 1 ? true : false,
            options: attributeData?.options,
            rules: attributeData?.rules,
            // tagged attribute properties
            tags: attributeData?.tags ?? [],
            shared: attributeData?.shared, // shareable flag;
        };
    };

    const getSubmissionTypeByAttributeId = (attributeId) => {
        // in case of confirm delete, submission type may not be present
        const existingAttribute = allAttributes.value?.find((attribute) => attribute.id === attributeId);
        if (existingAttribute.tags && existingAttribute.tags.length) {
            return AttributeSubmissionTypes.TAGGED;
        } else {
            return existingAttribute.company_id ? AttributeSubmissionTypes.COMPANY : AttributeSubmissionTypes.SHARED;
        }
    };

    const clearFilters = () => {
        params.value.code = null;
        params.value.shared = SharedTypes.COMPANY;
        params.value.default = true;
        params.value.user_profile = UserProfileTypes.NORMAL;
        params.value.tagged = null;
        params.value.category = null;
    };

    return {
        loading,
        // utils
        input_types,
        // states
        params,
        attribute,
        editId,
        attributes,
        totalAttributes,
        tags,
        // sharedTaggedAttributes,
        // getters
        allAttributes,
        allUserProfileAttributes,
        allTags,
        allSharedTaggedAttributes,
        // actions
        fetchAllAttributes,
        fetchAllUserProfileAttributes,
        fetchAttributeById,
        createAttribute,
        editAttribute,
        updateAttribute,
        deleteAttribute,
        setDefaultAttribute,
        transferAttributes,
        fetchAllTags,
        fetchAllSharedTaggedAttributes,
        loadAttributeFromTagId,
        // helpers
        initAttribute,
        clearFilters,
    };
});
